import { useQuery } from "@tanstack/react-query";
import { DateTime } from "luxon";
import { type FC, type ReactNode, useEffect, useState } from "react";
import { useFeatureFlag } from "../../hooks/useOpenFeatureFlag";
import { useScheduleDataUpdatedAt } from "../../hooks/useScheduleDataUpdatedAt";
import { useScheduleCacheEnabled } from "../../hooks/useScheduleWeekRules";
import { useShownWeek } from "../../hooks/useShownWeek";
import { backendService } from "../../services/backend.service";
import type { Recommendation, RecommendationParams } from "../../utils/models";
import { isHeadNurse } from "../../utils/utilFunctions";
import { useAuth } from "../AuthProvider";
import { FEATURE_FLAGS } from "../FeatureFlagsProvider";
import { useHospitalData } from "../HospitalDataProvider";
import { useRequestsContext } from "../RequestsContext";
import { RecommendationsContext } from "./RecommendationsContext";

const DAYS_PER_WEEK = 7;

export const RecommendationsProvider: FC<{ children: ReactNode }> = ({ children }) => {
	const { chosenDepartment, hospitalData, showErrorSnackbar } = useHospitalData();
	const { staffRequests } = useRequestsContext();
	const { weekStart } = useShownWeek();
	const { userInfo } = useAuth();

	const [recommendations, setRecommendations] = useState<Recommendation[]>([]);
	const endTime: DateTime | undefined = weekStart?.plus({ days: DAYS_PER_WEEK - 1 });
	const isCacheEnabled = useScheduleCacheEnabled();
	// The server reads the schedule itself; with the cache on, the shown week's cached days changing is what asks again.
	const shownWeekUpdatedAt = useScheduleDataUpdatedAt(chosenDepartment.department_id, weekStart, endTime);

	const {
		data: fetchedRecommendations,
		isLoading,
		error,
		refetch,
	} = useQuery<Recommendation[], Error>({
		queryKey: ["recommendations"],
		queryFn: async () => {
			if (!weekStart || !endTime) {
				throw new Error("Start time and end time are required");
			}

			const params: RecommendationParams = {
				departmentId: chosenDepartment.department_id,
				startDate: weekStart.toFormat("yyyy-MM-dd"),
				endDate: endTime.toFormat("yyyy-MM-dd"),
			};
			const { status, data: recommendations } = await backendService.getRecommendations(params);

			if (status >= 400 || !recommendations) throw new Error("Could not fetch recommendations");

			return recommendations;
		},
		enabled: false,
	});
	const isBlockRecommendationsV2Enabled = useFeatureFlag(FEATURE_FLAGS.BlockRecommendationsV2);

	const canFetch =
		isHeadNurse(userInfo.role) && !isBlockRecommendationsV2Enabled && Boolean(chosenDepartment.department_id && endTime && endTime > DateTime.now());

	// biome-ignore lint/correctness/useExhaustiveDependencies: should refetch when moving between weeks
	useEffect(() => {
		if (isCacheEnabled || !canFetch || !hospitalData.weeks.length || !hospitalData.nurses.length) return;
		const timeoutId = setTimeout(() => refetch(), 800);
		return () => clearTimeout(timeoutId);
	}, [userInfo, hospitalData, chosenDepartment, endTime?.toMillis(), refetch, staffRequests, isCacheEnabled]);

	// With the cache on, the department and the provider's data settle piece by piece after start (roster, units,
	// display date), so asking on each would send the same slow request several times. Asked once the shown week is in,
	// and again when the week, its cached days or the requests change.
	// biome-ignore lint/correctness/useExhaustiveDependencies: endTime is keyed by its time, so a new object for the same week doesn't ask again
	useEffect(() => {
		if (!isCacheEnabled || !canFetch || shownWeekUpdatedAt === 0) return;
		const timeoutId = setTimeout(() => refetch(), 800);
		return () => clearTimeout(timeoutId);
	}, [isCacheEnabled, canFetch, chosenDepartment.department_id, endTime?.toMillis(), staffRequests, shownWeekUpdatedAt, refetch]);

	useEffect(() => fetchedRecommendations && setRecommendations(fetchedRecommendations), [fetchedRecommendations]);

	// biome-ignore lint/correctness/useExhaustiveDependencies: showErrorSnackbar re-renders everythime
	useEffect(() => {
		if (error) {
			showErrorSnackbar("Oops! looks like we are unable to calculate your recommendations", {}, error);
			setRecommendations([]);
		}
	}, [error]);

	return <RecommendationsContext.Provider value={{ recommendations, setRecommendations, isLoading, error }}>{children}</RecommendationsContext.Provider>;
};
